import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Portal from '../Portal';
import { useTracking } from '../../hooks/useTracking';

interface GenerationResultModalProps {
  open: boolean;
  videoUrl: string;
  onClose: () => void;
  sourcePage?: 'lander-1' | 'image-to-video' | 'homepage'; // Add source page prop to determine button behavior
}

export default function GenerationResultModal({
  open,
  videoUrl,
  onClose,
  sourcePage = 'lander-1', // Default to lander-1 for backward compatibility
}: GenerationResultModalProps) {
  const [isNavigating, setIsNavigating] = useState(false);
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  const navigationIntentRef = useRef(false); // Guard against double navigation
  const navigate = useNavigate();
  const { track } = useTracking();

  // Lock scroll when overlay is open
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      
      // Prevent body scroll on mobile while modal open
      const preventScroll = (e: TouchEvent) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
        }
      };
      
      document.body.addEventListener('touchmove', preventScroll, { passive: false });
      
      return () => {
        document.body.style.overflow = '';
        document.body.removeEventListener('touchmove', preventScroll);
      };
    }
  }, [open]);

  if (!open) return null;

  const handleGenerateAnother = async () => {
    // Track button click immediately (before any guards) for accurate analytics
    track('lander1_remove_watermark_clicked', {
      source_page: sourcePage,
      video_url: videoUrl,
      has_url_hash: !!window.location.hash,
      timestamp: Date.now(),
    });
    
    // Prevent double-navigation (removed overly defensive userHasInteracted check)
    if (navigationIntentRef.current) {
      console.log('[GenerationResultModal] Navigation already in progress, ignoring duplicate click');
      return;
    }
    
    // Prevent double-click navigation
    if (isNavigating) {
      console.log('[GenerationResultModal] Navigation already in progress (state check), ignoring duplicate click');
      return;
    }
    
    navigationIntentRef.current = true;
    setIsNavigating(true);
    
    // CRITICAL: Wait for PostHog to queue the event before navigating
    // 100ms is enough for PostHog to queue the event (not send, just queue)
    // This prevents the race condition where navigation cancels pending events
    await new Promise(resolve => setTimeout(resolve, 100));
    
    if (sourcePage === 'image-to-video' || sourcePage === 'homepage') {
      // For homepage (formerly image-to-video page), refresh to reset the page state
      window.location.reload();
    } else {
      // For lander-1 page, navigate to upgrade page
      // OPTION C: Clean URL hash before navigation to prevent OAuth re-triggering
      if (window.location.hash) {
        console.log('[GenerationResultModal] 🧹 Cleaning URL hash before navigation to prevent OAuth issues');
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      
      // FIX 1: Clear lander-1 modal state before navigating to /upgrade
      // CRITICAL: This prevents the processing modal from appearing on upgrade page
      localStorage.removeItem('genanything_lander1_modal_state');
      console.log('[GenerationResultModal] 🧹 Cleared lander-1 modal state before navigating to /upgrade');
      
      // REVENUE PROTECTION: Use window.location.href instead of navigate()
      // This completely breaks the history chain and prevents back button from returning to /lander-1
      // Critical for mobile users from Meta ads who might accidentally hit back button
      console.log('[GenerationResultModal] Navigating to /upgrade (breaking history chain)');
      window.location.href = '/upgrade';
    }
  };

  return (
    <Portal>
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-3 md:p-4"
        onClick={(e) => {
          e.stopPropagation();
          // Prevent any backdrop clicks from propagating
        }}
        onTouchStart={(e) => {
          // iOS: Prevent touch from propagating through backdrop
          e.stopPropagation();
        }}
        onTouchEnd={(e) => {
          // Android: Prevent delayed click events
          e.stopPropagation();
        }}
      >
        <div className="w-full max-w-[90vw] h-[90vh] max-h-[90vh] flex flex-col">
          {/* Main Content Card */}
          <div className="bg-bg-panel border border-border rounded-modal shadow-lg flex flex-col h-full overflow-hidden">
            {/* Header */}
            <div className="flex justify-center items-center p-3 md:p-4 lg:p-6 flex-shrink-0 border-b border-border">
              <h2 className="text-h1 md:text-h1 lg:text-display font-medium text-white">
                🎉 Your Video is Complete!
              </h2>
            </div>

            {/* Video Player - Takes up available space */}
            <div className="flex-1 min-h-0 flex items-center justify-center p-3 md:p-4 lg:p-6">
              <div className="w-full h-full bg-black/20 rounded-lg overflow-hidden flex items-center justify-center">
                <video
                  src={videoUrl}
                  controls
                  autoPlay
                  className="w-full h-full object-contain rounded-lg"
                  onError={(e) => {
                    console.error('❌ [GenerationResultModal] Video load error:', e);
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>

            {/* Action Buttons - Fixed at bottom */}
            <div className="p-3 md:p-4 lg:p-6 flex-shrink-0 border-t border-border">
              {/* Generate Another Video Button - Full width */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setUserHasInteracted(true);
                  handleGenerateAnother();
                }}
                onTouchStart={(e) => {
                  // Mark interaction on touch start (iOS Safari needs this)
                  e.stopPropagation();
                  setUserHasInteracted(true);
                }}
                onTouchEnd={(e) => {
                  // Prevent ghost clicks that fire 300ms after touch on some Android browsers
                  e.stopPropagation();
                  e.preventDefault();
                  // CRITICAL: Call handler here since preventDefault blocks onClick on mobile
                  handleGenerateAnother();
                }}
                disabled={isNavigating}
                className="w-full bg-[#F9F7EA] text-[#0f1218] px-4 md:px-6 py-3 min-h-[44px] rounded-button font-medium hover:bg-[#E7D7B1] transition-colors flex items-center justify-center gap-2 text-body disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  className="w-4 h-4 md:w-5 md:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 4.5v15m7.5-7.5h-15"
                  />
                </svg>
                {sourcePage === 'lander-1' ? 'Remove Watermark' : 'Generate Another Video'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}
